import { FC, Suspense, lazy } from 'react';
import { Navigate } from 'react-router-dom';
import { ROLE_ACTIONS } from 'src/constants/roles';
import AuthGuard from 'src/guards/AuthGuard';
import RouteGuard from 'src/guards/RouteGuard';
import BaseLayout from 'src/layouts/BaseLayout';
import SidebarLayout from 'src/layouts/SidebarLayout';
import Unverified from 'src/pages/Fallbacks/Status/Unverified/Unverified';
import SuspenseLoader from 'switch-fe-shared/components/Suspense/SuspenseLoader';
import FeatureFlag from 'switch-fe-shared/FeatureFlag';

import DashboardRootRedirect from './DashboardRootRedirect';
import pages from './routes';

// Breadcrumb hierarchy levels
// 'system' - Pages outside of groups/apps (e.g., Application Groups)
// 'group' - Pages at group level (e.g., Applications, Users, Reports, Tariffs)
// 'app' - Pages at application level (default, e.g., Meters, Accounts, Dashboard)
export type BreadcrumbLevel = 'system' | 'group' | 'app';

// Route configuration map for breadcrumb hierarchy
// This map determines which hierarchy level each route belongs to
export const routeBreadcrumbConfig: Record<string, BreadcrumbLevel> = {
  // System-level pages (outside groups/apps)
  '/g': 'system',
  '/application-groups': 'system',
  '/providers': 'system',
  '/accounting': 'system',
  '/unallocated-devices': 'system',

  // Group-level pages (belong to a group but not a specific app)
  '/g/:groupId/dashboard': 'group',
  '/g/:groupId/a': 'group',
  '/g/:groupId/users': 'group',
  '/g/:groupId/api-keys': 'group',
  '/g/:groupId/reports': 'group',
  '/g/:groupId/tariffs': 'group',
  '/g/:groupId/integrations': 'group',
  '/applications': 'group',
  '/users': 'group',
  '/api-keys': 'group',
  '/reports': 'group',
  '/tariffs': 'group',
  '/g/:groupId/a/:appId/dashboard': 'app',

  // All other pages default to 'app' level (full hierarchy)
  // No need to list them here - anything not in this map defaults to 'app'
};

const Loader = (Component: FC) => (props: any) => (
  <Suspense fallback={<SuspenseLoader />}>
    <Component {...props} />
  </Suspense>
);

//TODO Change any
const lazyRetry = function (componentImport: any): Promise<any> {
  return new Promise((resolve, reject) => {
    // check if the window has already been refreshed
    const hasRefreshed = JSON.parse(
      window.sessionStorage.getItem('retry-lazy-refreshed') || 'false'
    );

    // try to import the component
    componentImport()
      .then((component: any) => {
        window.sessionStorage.setItem('retry-lazy-refreshed', 'false'); // success so reset the refresh
        resolve(component);
      })
      .catch((error: any) => {
        if (!hasRefreshed) {
          // not been refreshed yet
          window.sessionStorage.setItem('retry-lazy-refreshed', 'true'); // we are now going to refresh
          return window.location.reload(); // refresh the page
        }
        reject(error); // Default error behaviour as already tried refresh
      });
  });
};

// Pages
const Dashboard = Loader(
  lazy(() => lazyRetry(() => import('src/pages/Dashboard/pages/Dashboard')))
);
const Reports = Loader(
  lazy(() => lazyRetry(() => import('src/pages/Reporting')))
);
const Register = Loader(
  lazy(() => lazyRetry(() => import('src/pages/Register/Register')))
);
const Login = Loader(
  lazy(() => lazyRetry(() => import('src/pages/Login/Login')))
);
const TopUpSuccess = Loader(
  lazy(() => lazyRetry(() => import('src/pages/TopUp/TopUpSuccess')))
);
const TopUpError = Loader(
  lazy(() => lazyRetry(() => import('src/pages/TopUp/TopUpError')))
);
const TopUpCancel = Loader(
  lazy(() => lazyRetry(() => import('src/pages/TopUp/TopUpCancel')))
);
const TopUpReturn = Loader(
  lazy(() => lazyRetry(() => import('src/pages/TopUp/TopUpReturn')))
);
const ReleaseNotes = Loader(
  lazy(() => lazyRetry(() => import('src/pages/ReleaseNotes')))
);
const Integrations = Loader(
  lazy(() => lazyRetry(() => import('src/pages/Integrations')))
);
const Payments = Loader(
  lazy(() => lazyRetry(() => import('src/pages/Payments')))
);

// Invoices
const Invoices = Loader(
  lazy(() => lazyRetry(() => import('src/pages/Invoices')))
);
const InvoiceView = Loader(
  lazy(() => lazyRetry(() => import('src/pages/Invoices/View')))
);
const MeterJobs = Loader(
  lazy(() => lazyRetry(() => import('src/pages/Meters/MeterJobs')))
);

//Application Groups
const ApplicationGroups = Loader(
  lazy(() => lazyRetry(() => import('src/pages/ApplicationGroups')))
);
const ApplicationGroupCreate = Loader(
  lazy(() => lazyRetry(() => import('src/pages/ApplicationGroups/Create')))
);
const ApplicationGroupView = Loader(
  lazy(() => lazyRetry(() => import('src/pages/ApplicationGroups/View')))
);

//Applications
const Applications = Loader(
  lazy(() => lazyRetry(() => import('src/pages/Applications')))
);
const ApplicationView = Loader(
  lazy(() => lazyRetry(() => import('src/pages/Applications/View')))
);

// Accounts
const Accounts = Loader(
  lazy(() => lazyRetry(() => import('src/pages/Accounts')))
);
const AccountCreate = Loader(
  lazy(() => lazyRetry(() => import('src/pages/Accounts/Create')))
);
const AccountView = Loader(
  lazy(() => lazyRetry(() => import('src/pages/Accounts/View')))
);

// Tariffs
const Tariffs = Loader(
  lazy(() => lazyRetry(() => import('src/pages/Tariffs')))
);
const TariffCreate = Loader(
  lazy(() => lazyRetry(() => import('src/pages/Tariffs/Create')))
);
const TariffView = Loader(
  lazy(() => lazyRetry(() => import('src/pages/Tariffs/View')))
);

// Utilities
const Utilities = Loader(
  lazy(() => lazyRetry(() => import('src/pages/Utilities')))
);
const UtilityCreate = Loader(
  lazy(() => lazyRetry(() => import('src/pages/Utilities/Create')))
);
const UtilityView = Loader(
  lazy(() => lazyRetry(() => import('src/pages/Utilities/View')))
);

// Sensors
const Sensors = Loader(
  lazy(() => lazyRetry(() => import('src/pages/Sensors')))
);
const SensorView = Loader(
  lazy(() => lazyRetry(() => import('src/pages/Sensors/View')))
);

// Meters
const Meters = Loader(lazy(() => lazyRetry(() => import('src/pages/Meters'))));
const MeterCreate = Loader(
  lazy(() => lazyRetry(() => import('src/pages/Meters/Create')))
);
const MeterView = Loader(
  lazy(() => lazyRetry(() => import('src/pages/Meters/View')))
);
const UnallocatedDevices = Loader(
  lazy(() =>
    lazyRetry(() => import('src/pages/UnallocatedDevices/UnallocatedDevices'))
  )
);

// Meter Points
const MeterPoints = Loader(
  lazy(() => lazyRetry(() => import('src/pages/MeterPoints')))
);
const MeterPointCreate = Loader(
  lazy(() => lazyRetry(() => import('src/pages/MeterPoints/Create')))
);
const MeterPointView = Loader(
  lazy(() => lazyRetry(() => import('src/pages/MeterPoints/View')))
);

// Gateways
const Gateways = Loader(
  lazy(() => lazyRetry(() => import('src/pages/Gateways')))
);
const GatewayView = Loader(
  lazy(() => lazyRetry(() => import('src/pages/Gateways/View')))
);
const GatewayCreate = Loader(
  lazy(() => lazyRetry(() => import('src/pages/Gateways/Create')))
);

//Zones
const Zones = Loader(lazy(() => lazyRetry(() => import('src/pages/Zones'))));
const ZoneCreate = Loader(
  lazy(() => lazyRetry(() => import('src/pages/Zones/Create')))
);
const ZoneView = Loader(
  lazy(() => lazyRetry(() => import('src/pages/Zones/View')))
);

//Users
const Users = Loader(lazy(() => lazyRetry(() => import('src/pages/Users'))));

const UserView = Loader(
  lazy(() => lazyRetry(() => import('src/pages/Users/View')))
);

const ApiKeys = Loader(
  lazy(() => lazyRetry(() => import('src/pages/ApiKeys')))
);

// Emails
const EmailIntegrationLogs = Loader(
  lazy(() =>
    lazyRetry(
      () => import('src/pages/EmailIntegrationLogs/pages/EmailIntegrationLogs')
    )
  )
);
const EmailIntegrationWhitelists = Loader(
  lazy(() =>
    lazyRetry(
      () =>
        import('src/pages/EmailIntegrationLogs/pages/EmailIntegrationWhitelist')
    )
  )
);

// Accounting
const Accounting = Loader(
  lazy(() => lazyRetry(() => import('src/pages/Accounting')))
);
const AccountingView = Loader(
  lazy(() => lazyRetry(() => import('src/pages/Accounting/View')))
);

// Other
const MapPage = Loader(
  lazy(() => lazyRetry(() => import('src/pages/Map/Map')))
);
const Financials = Loader(
  lazy(() => lazyRetry(() => import('src/pages/Payments/Financials')))
);
const ReconciliationPage = Loader(
  lazy(() => lazyRetry(() => import('src/pages/Reconciliation')))
);
const ReconView = Loader(
  lazy(() => lazyRetry(() => import('src/pages/Reconciliation/View')))
);
const ManualReadings = Loader(
  lazy(() => lazyRetry(() => import('src/pages/ManualReadings')))
);

// Providers
const Providers = Loader(
  lazy(() => lazyRetry(() => import('src/pages/Providers')))
);
const ProviderView = Loader(
  lazy(() => lazyRetry(() => import('src/pages/Providers/View')))
);
const ProviderCreate = Loader(
  lazy(() => lazyRetry(() => import('src/pages/Providers/Create')))
);

// Status
const Status404 = Loader(
  lazy(() => import('src/pages/Fallbacks/Status/Status404/Status404'))
);
const Status500 = Loader(
  lazy(() => import('src/pages/Fallbacks/Status/Status500/Status500'))
);
const StatusComingSoon = Loader(
  lazy(() => import('src/pages/Fallbacks/Status/ComingSoon/ComingSoon'))
);
const StatusMaintenance = Loader(
  lazy(() => import('src/pages/Fallbacks/Status/Maintenance/Maintenance'))
);
const StatusSuccess = Loader(
  lazy(() => import('src/pages/Fallbacks/Status/Success/Success'))
);
const StatusFailure = Loader(
  lazy(() => import('src/pages/Fallbacks/Status/Failure/Failure'))
);
const StatusCancel = Loader(
  lazy(() => import('src/pages/Fallbacks/Status/Cancel/Cancel'))
);
const StatusUnauthorized = Loader(
  lazy(() => import('src/pages/Fallbacks/Status/Unauthorized/Unauthorized'))
);

// Dev-only playground
const SupportedMetersPage = Loader(
  lazy(() => lazyRetry(() => import('src/pages/SupportedMeters')))
);
const Design = Loader(
  lazy(() => lazyRetry(() => import('src/pages/Design/index')))
);
const DesignTokens = Loader(
  lazy(() => lazyRetry(() => import('src/pages/Design/pages/Tokens')))
);
const DesignCharts = Loader(
  lazy(() => lazyRetry(() => import('src/pages/Design/pages/Charts')))
);
const DesignGantt = Loader(
  lazy(() => lazyRetry(() => import('src/pages/Design/pages/GanttHarness')))
);
const DesignBulkActions = Loader(
  lazy(() => lazyRetry(() => import('src/pages/Design/pages/BulkActions')))
);
const DesignBulkEditWizard = Loader(
  lazy(() =>
    lazyRetry(() => import('src/pages/Design/pages/BulkEditWizardHarness'))
  )
);
const DesignBulkRename = Loader(
  lazy(() => lazyRetry(() => import('src/pages/Design/pages/BulkRename')))
);
const DesignPpaSite = Loader(
  lazy(() => lazyRetry(() => import('src/pages/Design/pages/PpaSite')))
);
const DesignRuleDialog = Loader(
  lazy(() =>
    lazyRetry(() => import('src/pages/Design/pages/RuleDialogHarness'))
  )
);

const isDev = process.env.REACT_APP_DEPLOYMENT_ENV === 'development';

const routes: any[] = [
  {
    path: '',
    element: <BaseLayout />,
    /**
     * All children within this element will NOT have a Sidebar and top Navbar
     * All children within this element does not need to be authenticated to access
     */
    children: [
      //#region Root
      {
        path: '/',
        element: <DashboardRootRedirect />,
      },
      //#endregion Root
      //#region Auth
      {
        //All authentication routes
        //No navbars are shown as the user is not logged in
        path: pages.auth.root,
        children: [
          {
            path: '',
            element: <Login />,
          },
          {
            path: pages.auth.login.name,
            element: <Login />,
          },
          {
            path: pages.auth.register.name,
            element: <Register />,
          },
        ],
      },
      //#endregion Auth
      //#region TopUp
      {
        path: pages.topup.root,
        children: [
          {
            path: pages.topup.success.name,
            element: <TopUpSuccess />,
          },
          {
            path: pages.topup.error.name,
            element: <TopUpError />,
          },
          {
            path: pages.topup.cancel.name,
            element: <TopUpCancel />,
          },
          {
            path: pages.topup.return.name,
            element: <TopUpReturn />,
          },
        ],
      },
      //#endregion TopUp
      //#region ReleaseNotes
      {
        path: pages.releaseNotes.path,
        element: <ReleaseNotes />,
      },
      //#endregion ReleaseNotes
      //#region Status
      {
        //All status routes
        path: pages.status.root,
        children: [
          {
            path: '',
            element: <Navigate to='404' replace />,
          },
          {
            path: pages.status.unverified.name,
            element: <Unverified />,
          },
          {
            path: pages.status.unauthorized.name,
            element: <StatusUnauthorized />,
          },
          {
            path: pages.status.status404.name,
            element: <Status404 />,
          },
          {
            path: pages.status.status500.name,
            element: <Status500 />,
          },
          {
            path: pages.status.statusMaintenance.name,
            element: <StatusMaintenance />,
          },
          {
            path: pages.status.statusComingSoon.name,
            element: <StatusComingSoon />,
          },
          //TODO: Maybe make paths that shows status within the sidebars as well
          {
            path: pages.status.statusSuccess.name,
            element: <StatusSuccess />,
          },
          {
            path: pages.status.statusFailure.name,
            element: <StatusFailure />,
          },
          {
            path: pages.status.statusCancel.name,
            element: <StatusCancel />,
          },
        ],
      },
      //#endregion Status
      // Dev-only routes
      ...(isDev
        ? [
            {
              path: '/design',
              element: <Design />,
            },
            {
              path: '/design/tokens',
              element: <DesignTokens />,
            },
            {
              path: '/design/charts',
              element: <DesignCharts />,
            },
            {
              path: '/design/gantt',
              element: <DesignGantt />,
            },
            {
              path: '/design/bulk-actions',
              element: <DesignBulkActions />,
            },
            {
              path: '/design/bulk-rename',
              element: <DesignBulkRename />,
            },
            {
              path: '/design/rule-dialog',
              element: <DesignRuleDialog />,
            },
            {
              path: '/design/bulk-edit-wizard',
              element: <DesignBulkEditWizard />,
            },
            {
              path: '/design/ppa-site',
              element: <DesignPpaSite />,
            },
          ]
        : []),
      //#region NotFound
      {
        path: '*',
        element: <Status404 />,
      },
      //#endregion NotFound
    ],
  },
  {
    /**
     * Authenticated but chrome-less: requires login (AuthGuard) yet renders
     * standalone via BaseLayout, so the page keeps its own full-screen header
     * instead of the app sidebar and breadcrumb.
     */
    path: '',
    element: (
      <AuthGuard>
        <BaseLayout />
      </AuthGuard>
    ),
    children: [
      {
        path: pages.hardware.path,
        element: <SupportedMetersPage />,
      },
    ],
  },
  {
    path: '',
    element: (
      /**
       * All children with this element will have a Sidebar and top Navbar
       * AuthGuard checks that the user is logged in before granting access to its children pages
       */
      <AuthGuard>
        <SidebarLayout />
      </AuthGuard>
    ),
    children: [
      //#region Base
      {
        path: '',
        element: <Navigate to={pages.dashboard.name} replace />,
      },
      //#endregion Base
      //#region Dashboard
      {
        path: pages.dashboard.name,
        element: <Dashboard />,
      },
      {
        path: pages.home.name,
        element: <Dashboard />,
      },
      //#endregion Dashboard
      {
        path: '/g',
        breadcrumb: 'Application Groups',
        children: [
          {
            path: '',
            index: true,
            element: (
              <RouteGuard
                allowedActions={ROLE_ACTIONS.READ}
                subject='ApplicationGroups'
              >
                <ApplicationGroups />
              </RouteGuard>
            ),
          },
          {
            path: 'create',
            element: (
              <RouteGuard
                allowedActions={ROLE_ACTIONS.CREATE}
                subject='ApplicationGroups'
              >
                <ApplicationGroupCreate />
              </RouteGuard>
            ),
          },
          {
            path: ':groupId',
            element: (
              <RouteGuard
                allowedActions={ROLE_ACTIONS.UPDATE}
                subject='ApplicationGroups'
              >
                <ApplicationGroupView />
              </RouteGuard>
            ),
          },
          {
            path: ':groupId/dashboard',
            element: <Dashboard />,
          },
          {
            path: ':groupId/a',
            children: [
              {
                path: '',
                index: true,
                element: (
                  <RouteGuard
                    allowedActions={ROLE_ACTIONS.READ}
                    subject='Applications'
                  >
                    <Applications />
                  </RouteGuard>
                ),
              },
              {
                path: ':appId',
                children: [
                  {
                    path: '',
                    index: true,
                    element: (
                      <RouteGuard
                        allowedActions={ROLE_ACTIONS.READ}
                        subject='Applications'
                      >
                        <ApplicationView />
                      </RouteGuard>
                    ),
                  },
                  {
                    path: 'dashboard',
                    element: <Dashboard />,
                  },
                  {
                    path: 'accounts',
                    children: [
                      {
                        path: '',
                        index: true,
                        element: (
                          <RouteGuard
                            allowedActions={ROLE_ACTIONS.READ}
                            subject='Accounts'
                          >
                            <Accounts />
                          </RouteGuard>
                        ),
                      },
                      {
                        path: 'create',
                        element: (
                          <RouteGuard
                            allowedActions={ROLE_ACTIONS.CREATE}
                            subject='Accounts'
                          >
                            <AccountCreate />
                          </RouteGuard>
                        ),
                      },
                      {
                        path: ':id',
                        element: (
                          <RouteGuard
                            allowedActions={ROLE_ACTIONS.READ}
                            subject='Accounts'
                          >
                            <AccountView />
                          </RouteGuard>
                        ),
                      },
                    ],
                  },
                  {
                    path: 'utilities',
                    children: [
                      {
                        path: '',
                        index: true,
                        element: (
                          <RouteGuard
                            allowedActions={ROLE_ACTIONS.READ}
                            subject='ApplicationUtilities'
                          >
                            <Utilities />
                          </RouteGuard>
                        ),
                      },
                      {
                        path: 'create',
                        element: (
                          <RouteGuard
                            allowedActions={ROLE_ACTIONS.CREATE}
                            subject='ApplicationUtilities'
                          >
                            <UtilityCreate />
                          </RouteGuard>
                        ),
                      },
                      {
                        path: ':id',
                        element: (
                          <RouteGuard
                            allowedActions={ROLE_ACTIONS.READ}
                            subject='ApplicationUtilities'
                          >
                            <UtilityView />
                          </RouteGuard>
                        ),
                      },
                    ],
                  },
                  {
                    path: pages.meterpoints.name,
                    children: [
                      {
                        path: '',
                        index: true,
                        element: (
                          <RouteGuard
                            allowedActions={ROLE_ACTIONS.READ}
                            subject='MeterPoints'
                          >
                            <MeterPoints />
                          </RouteGuard>
                        ),
                      },
                      {
                        path: 'create',
                        element: (
                          <RouteGuard
                            allowedActions={ROLE_ACTIONS.CREATE}
                            subject='MeterPoints'
                          >
                            <MeterPointCreate />
                          </RouteGuard>
                        ),
                      },
                      {
                        path: ':id',
                        element: (
                          <RouteGuard
                            allowedActions={ROLE_ACTIONS.READ}
                            subject='MeterPoints'
                          >
                            <MeterPointView />
                          </RouteGuard>
                        ),
                      },
                    ],
                  },
                  {
                    path: 'meters',
                    children: [
                      {
                        path: '',
                        index: true,
                        element: (
                          <RouteGuard
                            allowedActions={ROLE_ACTIONS.READ}
                            subject='Meters'
                          >
                            <Meters />
                          </RouteGuard>
                        ),
                      },
                      {
                        path: 'create',
                        element: (
                          <RouteGuard
                            allowedActions={ROLE_ACTIONS.CREATE}
                            subject='Meters'
                          >
                            <MeterCreate />
                          </RouteGuard>
                        ),
                      },
                      {
                        path: 'jobs',
                        element: (
                          <RouteGuard
                            allowedActions={ROLE_ACTIONS.MANAGE}
                            subject='Meters'
                          >
                            <MeterJobs />
                          </RouteGuard>
                        ),
                      },
                      {
                        path: ':id',
                        element: (
                          <RouteGuard
                            allowedActions={ROLE_ACTIONS.READ}
                            subject='Meters'
                          >
                            <MeterView />
                          </RouteGuard>
                        ),
                      },
                    ],
                  },
                  {
                    path: 'gateways',
                    children: [
                      {
                        path: '',
                        index: true,
                        element: (
                          <RouteGuard
                            allowedActions={ROLE_ACTIONS.READ}
                            subject='Gateways'
                          >
                            <Gateways />
                          </RouteGuard>
                        ),
                      },
                      {
                        path: 'create',
                        element: (
                          <RouteGuard
                            allowedActions={ROLE_ACTIONS.CREATE}
                            subject='Gateways'
                          >
                            <GatewayCreate />
                          </RouteGuard>
                        ),
                      },
                      {
                        path: ':id',
                        element: (
                          <RouteGuard
                            allowedActions={ROLE_ACTIONS.READ}
                            subject='Gateways'
                          >
                            <GatewayView />
                          </RouteGuard>
                        ),
                      },
                    ],
                  },
                  {
                    path: 'sensors',
                    children: [
                      {
                        path: '',
                        index: true,
                        element: (
                          <RouteGuard
                            allowedActions={ROLE_ACTIONS.READ}
                            subject='Sensors'
                          >
                            <Sensors />
                          </RouteGuard>
                        ),
                      },
                      {
                        path: ':id',
                        element: (
                          <RouteGuard
                            allowedActions={ROLE_ACTIONS.READ}
                            subject='Sensors'
                          >
                            <SensorView />
                          </RouteGuard>
                        ),
                      },
                    ],
                  },
                  {
                    path: 'zones',
                    children: [
                      {
                        path: '',
                        index: true,
                        element: (
                          <RouteGuard
                            allowedActions={ROLE_ACTIONS.READ}
                            subject='Zones'
                          >
                            <Zones />
                          </RouteGuard>
                        ),
                      },
                      {
                        path: 'create',
                        element: (
                          <RouteGuard
                            allowedActions={ROLE_ACTIONS.CREATE}
                            subject='Zones'
                          >
                            <ZoneCreate />
                          </RouteGuard>
                        ),
                      },
                      {
                        path: ':id',
                        element: (
                          <RouteGuard
                            allowedActions={ROLE_ACTIONS.READ}
                            subject='Zones'
                          >
                            <ZoneView />
                          </RouteGuard>
                        ),
                      },
                    ],
                  },
                  {
                    path: 'manual-data',
                    children: [
                      {
                        path: '',
                        index: true,
                        element: (
                          <RouteGuard
                            allowedActions={ROLE_ACTIONS.MANAGE}
                            subject='UtilityData'
                          >
                            <ManualReadings />
                          </RouteGuard>
                        ),
                      },
                      {
                        path: 'whitelists',
                        element: (
                          <RouteGuard
                            allowedActions={ROLE_ACTIONS.MANAGE}
                            subject='UtilityData'
                          >
                            <EmailIntegrationWhitelists />
                          </RouteGuard>
                        ),
                      },
                    ],
                  },
                  {
                    path: 'payments',
                    children: [
                      {
                        path: '',
                        index: true,
                        element: (
                          <RouteGuard
                            allowedActions={ROLE_ACTIONS.READ}
                            subject='Payments'
                          >
                            <Payments />
                          </RouteGuard>
                        ),
                      },
                      {
                        path: 'financials',
                        element: (
                          <RouteGuard
                            allowedActions={ROLE_ACTIONS.READ}
                            subject='Payments'
                          >
                            <Financials />
                          </RouteGuard>
                        ),
                      },
                    ],
                  },
                  {
                    path: 'invoices',
                    children: [
                      {
                        path: '',
                        index: true,
                        element: (
                          <FeatureFlag
                            flag='ac_invoicing_overhaul__release'
                            fallback={<Status404 />}
                          >
                            <RouteGuard
                              allowedActions={ROLE_ACTIONS.READ}
                              subject='Invoices'
                            >
                              <Invoices />
                            </RouteGuard>
                          </FeatureFlag>
                        ),
                      },
                      {
                        path: ':id',
                        element: (
                          <FeatureFlag
                            flag='ac_invoicing_overhaul__release'
                            fallback={<Status404 />}
                          >
                            <RouteGuard
                              allowedActions={ROLE_ACTIONS.READ}
                              subject='Invoices'
                            >
                              <InvoiceView />
                            </RouteGuard>
                          </FeatureFlag>
                        ),
                      },
                    ],
                  },
                  {
                    path: 'recons',
                    children: [
                      {
                        path: '',
                        index: true,
                        element: (
                          <RouteGuard
                            allowedActions={ROLE_ACTIONS.MANAGE}
                            subject='all'
                          >
                            <ReconciliationPage />
                          </RouteGuard>
                        ),
                      },
                      {
                        path: ':id',
                        element: (
                          <RouteGuard
                            allowedActions={ROLE_ACTIONS.MANAGE}
                            subject='all'
                          >
                            <ReconciliationPage />
                            <ReconView />
                          </RouteGuard>
                        ),
                      },
                    ],
                  },
                  {
                    path: 'map',
                    element: (
                      <RouteGuard
                        allowedActions={ROLE_ACTIONS.READ}
                        subject='Meters'
                      >
                        <MapPage />
                      </RouteGuard>
                    ),
                  },
                  {
                    path: 'email-integration-logs',
                    element: (
                      <RouteGuard
                        allowedActions={ROLE_ACTIONS.MANAGE}
                        subject='all'
                      >
                        <EmailIntegrationLogs />
                      </RouteGuard>
                    ),
                  },
                ],
              },
            ],
          },
          {
            path: ':groupId/users',
            children: [
              {
                path: '',
                index: true,
                element: (
                  <RouteGuard
                    allowedActions={ROLE_ACTIONS.MANAGE}
                    subject='Users'
                  >
                    <Users />
                  </RouteGuard>
                ),
              },
              {
                path: ':id',
                element: (
                  <RouteGuard
                    allowedActions={ROLE_ACTIONS.MANAGE}
                    subject='Users'
                  >
                    <UserView />
                  </RouteGuard>
                ),
              },
            ],
          },
          {
            path: ':groupId/api-keys',
            element: (
              <RouteGuard
                allowedActions={ROLE_ACTIONS.MANAGE}
                subject='ApplicationGroups'
              >
                <ApiKeys />
              </RouteGuard>
            ),
          },
          {
            path: ':groupId/reports',
            element: (
              <RouteGuard allowedActions={ROLE_ACTIONS.READ} subject='Reports'>
                <Reports />
              </RouteGuard>
            ),
          },
          {
            path: ':groupId/tariffs',
            children: [
              {
                path: '',
                index: true,
                element: (
                  <RouteGuard
                    allowedActions={ROLE_ACTIONS.READ}
                    subject='Tariffs'
                  >
                    <Tariffs />
                  </RouteGuard>
                ),
              },
              {
                path: 'create',
                element: (
                  <RouteGuard
                    allowedActions={ROLE_ACTIONS.CREATE}
                    subject='Tariffs'
                  >
                    <TariffCreate />
                  </RouteGuard>
                ),
              },
              {
                path: ':id',
                element: (
                  <RouteGuard
                    allowedActions={ROLE_ACTIONS.READ}
                    subject='Tariffs'
                  >
                    <TariffView />
                  </RouteGuard>
                ),
              },
            ],
          },
          {
            path: ':groupId/integrations',
            element: (
              <RouteGuard
                allowedActions={ROLE_ACTIONS.MANAGE}
                subject='Applications'
              >
                <Integrations />
              </RouteGuard>
            ),
          },
        ],
      },
      {
        path: pages.applicationGroup.path,
        breadcrumb: 'Application Groups',
        children: [
          {
            path: '',
            index: true,
            element: (
              <RouteGuard
                allowedActions={ROLE_ACTIONS.READ}
                subject='ApplicationGroups'
              >
                <ApplicationGroups />
              </RouteGuard>
            ),
          },
          {
            path: ':id',
            element: (
              <RouteGuard
                allowedActions={ROLE_ACTIONS.UPDATE}
                subject='ApplicationGroups'
              >
                <ApplicationGroupView />
              </RouteGuard>
            ),
          },
          {
            path: 'create',
            element: (
              <RouteGuard
                allowedActions={ROLE_ACTIONS.CREATE}
                subject='ApplicationGroups'
              >
                <ApplicationGroupCreate />
              </RouteGuard>
            ),
          },
        ],
      },
      {
        path: pages.providers.path,
        breadcrumb: 'Providers',
        children: [
          {
            path: '',
            index: true,
            element: (
              <RouteGuard allowedActions={ROLE_ACTIONS.MANAGE} subject='all'>
                <Providers />
              </RouteGuard>
            ),
          },
          {
            path: 'create',
            element: (
              <RouteGuard allowedActions={ROLE_ACTIONS.MANAGE} subject='all'>
                <ProviderCreate />
              </RouteGuard>
            ),
          },
          {
            path: ':id',
            element: (
              <RouteGuard allowedActions={ROLE_ACTIONS.MANAGE} subject='all'>
                <ProviderView />
              </RouteGuard>
            ),
          },
        ],
      },
      {
        path: pages.zones.path,
        children: [
          {
            path: '',
            index: true,
            element: (
              <RouteGuard allowedActions={ROLE_ACTIONS.READ} subject='Zones'>
                <Zones />
              </RouteGuard>
            ),
          },
          {
            path: ':id',
            element: (
              <RouteGuard allowedActions={ROLE_ACTIONS.READ} subject='Zones'>
                <ZoneView />
              </RouteGuard>
            ),
          },
          {
            path: 'create',
            element: (
              <RouteGuard allowedActions={ROLE_ACTIONS.CREATE} subject='Zones'>
                <ZoneCreate />
              </RouteGuard>
            ),
          },
        ],
      },
      {
        path: pages.application.path,
        children: [
          {
            path: '',
            index: true,
            element: (
              <RouteGuard
                allowedActions={ROLE_ACTIONS.READ}
                subject='Applications'
              >
                <Applications />
              </RouteGuard>
            ),
          },
          {
            path: ':id',
            element: (
              <RouteGuard
                allowedActions={ROLE_ACTIONS.READ}
                subject='Applications'
              >
                <ApplicationView />
              </RouteGuard>
            ),
          },
        ],
      },
      {
        path: pages.accounts.path,
        children: [
          {
            path: '',
            index: true,
            element: (
              <RouteGuard allowedActions={ROLE_ACTIONS.READ} subject='Accounts'>
                <Accounts />
              </RouteGuard>
            ),
          },
          {
            path: 'create',
            element: (
              <RouteGuard
                allowedActions={ROLE_ACTIONS.CREATE}
                subject='Accounts'
              >
                <AccountCreate />
              </RouteGuard>
            ),
          },
          {
            path: ':id',
            element: (
              <RouteGuard allowedActions={ROLE_ACTIONS.READ} subject='Accounts'>
                <AccountView />
              </RouteGuard>
            ),
          },
        ],
      },
      {
        path: pages.sensors.path,
        children: [
          {
            path: '',
            index: true,
            element: (
              <RouteGuard
                allowedActions={ROLE_ACTIONS.READ}
                subject={'Sensors'}
              >
                <Sensors />
              </RouteGuard>
            ),
          },
          {
            path: ':id',
            element: (
              <RouteGuard allowedActions={ROLE_ACTIONS.READ} subject='Sensors'>
                <SensorView />
              </RouteGuard>
            ),
          },
        ],
      },
      {
        path: pages.meters.path,
        children: [
          {
            path: '',
            index: true,
            element: (
              <RouteGuard allowedActions={ROLE_ACTIONS.READ} subject='Meters'>
                <Meters />
              </RouteGuard>
            ),
          },
          {
            path: 'create',
            element: (
              <RouteGuard allowedActions={ROLE_ACTIONS.CREATE} subject='Meters'>
                <MeterCreate />
              </RouteGuard>
            ),
          },
          {
            path: ':id',
            element: (
              <RouteGuard allowedActions={ROLE_ACTIONS.READ} subject='Meters'>
                <MeterView />
              </RouteGuard>
            ),
          },
          {
            path: 'jobs',
            element: (
              <RouteGuard allowedActions={ROLE_ACTIONS.MANAGE} subject='Meters'>
                <MeterJobs />
              </RouteGuard>
            ),
          },
        ],
      },
      {
        path: pages.meterpoints.path,
        breadcrumb: 'Meter Points',
        children: [
          {
            path: '',
            index: true,
            element: (
              <RouteGuard
                allowedActions={ROLE_ACTIONS.READ}
                subject='MeterPoints'
              >
                <MeterPoints />
              </RouteGuard>
            ),
          },
          {
            path: 'create',
            element: (
              <RouteGuard
                allowedActions={ROLE_ACTIONS.CREATE}
                subject='MeterPoints'
              >
                <MeterPointCreate />
              </RouteGuard>
            ),
          },
          {
            path: ':id',
            element: (
              <RouteGuard
                allowedActions={ROLE_ACTIONS.READ}
                subject='MeterPoints'
              >
                <MeterPointView />
              </RouteGuard>
            ),
          },
        ],
      },
      {
        path: pages.gateways.path,
        children: [
          {
            path: '',
            index: true,
            element: (
              <RouteGuard allowedActions={ROLE_ACTIONS.READ} subject='Gateways'>
                <Gateways />
              </RouteGuard>
            ),
          },
          {
            path: 'create',
            element: (
              <RouteGuard
                allowedActions={ROLE_ACTIONS.CREATE}
                subject='Gateways'
              >
                <GatewayCreate />
              </RouteGuard>
            ),
          },
          {
            path: ':id',
            element: (
              <RouteGuard allowedActions={ROLE_ACTIONS.READ} subject='Gateways'>
                {/* <GatewayInfo /> */}
                <GatewayView />
              </RouteGuard>
            ),
          },
          // {
          //     path:':imei/logs',
          //     element: (
          //         <RouteGuard allowedActions={ROLE_ACTIONS.READ} subject='Gateways'>
          //             <ResponseLogs />
          //         </RouteGuard>
          //     )
          // },
        ],
      },
      {
        path: pages.utility.path,
        children: [
          {
            path: '',
            index: true,
            element: (
              <RouteGuard
                allowedActions={ROLE_ACTIONS.READ}
                subject='ApplicationUtilities'
              >
                <Utilities />
              </RouteGuard>
            ),
          },
          {
            path: 'create',
            element: (
              <RouteGuard
                allowedActions={ROLE_ACTIONS.CREATE}
                subject='ApplicationUtilities'
              >
                <UtilityCreate />
              </RouteGuard>
            ),
          },
          {
            path: ':id',
            element: (
              <RouteGuard
                allowedActions={ROLE_ACTIONS.READ}
                subject='ApplicationUtilities'
              >
                <UtilityView />
              </RouteGuard>
            ),
          },
        ],
      },
      {
        path: pages.payments.path,
        children: [
          {
            path: '',
            index: true,
            element: (
              <RouteGuard allowedActions={ROLE_ACTIONS.READ} subject='Payments'>
                <Payments />
              </RouteGuard>
            ),
          },
          {
            path: 'financials',
            element: (
              <RouteGuard allowedActions={ROLE_ACTIONS.READ} subject='Payments'>
                <Financials />
              </RouteGuard>
            ),
          },
        ],
      },
      {
        path: pages.invoices.path,
        children: [
          {
            path: '',
            index: true,
            element: (
              <FeatureFlag
                flag='ac_invoicing_overhaul__release'
                fallback={<Status404 />}
              >
                <RouteGuard
                  allowedActions={ROLE_ACTIONS.READ}
                  subject='Invoices'
                >
                  <Invoices />
                </RouteGuard>
              </FeatureFlag>
            ),
          },
          {
            path: ':id',
            element: (
              <FeatureFlag
                flag='ac_invoicing_overhaul__release'
                fallback={<Status404 />}
              >
                <RouteGuard
                  allowedActions={ROLE_ACTIONS.READ}
                  subject='Invoices'
                >
                  <InvoiceView />
                </RouteGuard>
              </FeatureFlag>
            ),
          },
        ],
      },
      {
        path: pages.recons.path,
        children: [
          {
            path: '',
            index: true,
            element: (
              <RouteGuard allowedActions={ROLE_ACTIONS.MANAGE} subject='all'>
                <ReconciliationPage />
              </RouteGuard>
            ),
          },
          {
            path: ':id',
            element: (
              <RouteGuard allowedActions={ROLE_ACTIONS.MANAGE} subject='all'>
                <ReconciliationPage />
                <ReconView />
              </RouteGuard>
            ),
          },
        ],
      },
      {
        path: pages.map.path,
        children: [
          {
            path: '',
            index: true,
            element: (
              <RouteGuard allowedActions={ROLE_ACTIONS.READ} subject='Meters'>
                <MapPage />
              </RouteGuard>
            ),
          },
        ],
      },
      {
        path: pages.manualReadings.path,
        children: [
          {
            path: '',
            index: true,
            element: (
              <RouteGuard
                allowedActions={ROLE_ACTIONS.MANAGE}
                subject='UtilityData'
              >
                <ManualReadings />
              </RouteGuard>
            ),
          },
          {
            path: 'whitelists',
            element: (
              <RouteGuard
                allowedActions={ROLE_ACTIONS.MANAGE}
                subject='UtilityData'
              >
                <EmailIntegrationWhitelists />
              </RouteGuard>
            ),
          },
        ],
      },
      {
        path: pages.unallocatedDevices.path,
        children: [
          {
            path: '',
            index: true,
            element: (
              <RouteGuard allowedActions={ROLE_ACTIONS.MANAGE} subject='all'>
                <UnallocatedDevices />
              </RouteGuard>
            ),
          },
        ],
      },
      {
        path: pages.users.path,
        children: [
          {
            path: '',
            index: true,
            element: (
              <RouteGuard allowedActions={ROLE_ACTIONS.MANAGE} subject='Users'>
                <Users />
              </RouteGuard>
            ),
          },
          {
            path: ':id',
            element: (
              <RouteGuard allowedActions={ROLE_ACTIONS.MANAGE} subject='Users'>
                <UserView />
              </RouteGuard>
            ),
          },
        ],
      },
      {
        path: pages.apiKeys.path,
        element: (
          <RouteGuard
            allowedActions={ROLE_ACTIONS.MANAGE}
            subject='ApplicationGroups'
          >
            <ApiKeys />
          </RouteGuard>
        ),
      },
      {
        path: pages.tariffs.path,
        children: [
          {
            path: '',
            index: true,
            element: (
              <RouteGuard allowedActions={ROLE_ACTIONS.READ} subject='Tariffs'>
                <Tariffs />
              </RouteGuard>
            ),
          },
          {
            path: 'create',
            element: (
              <RouteGuard
                allowedActions={ROLE_ACTIONS.CREATE}
                subject='Tariffs'
              >
                <TariffCreate />
              </RouteGuard>
            ),
          },
          {
            path: ':id',
            element: (
              <RouteGuard allowedActions={ROLE_ACTIONS.READ} subject='Tariffs'>
                <TariffView />
              </RouteGuard>
            ),
          },
        ],
      },
      {
        path: pages.accounting.path,
        breadcrumb: 'Accounting',
        children: [
          {
            path: '',
            index: true,
            element: (
              <RouteGuard allowedActions={ROLE_ACTIONS.MANAGE} subject='all'>
                <Accounting />
              </RouteGuard>
            ),
          },
          {
            path: 'accounts/:id',
            element: (
              <RouteGuard allowedActions={ROLE_ACTIONS.MANAGE} subject='all'>
                <AccountingView />
              </RouteGuard>
            ),
          },
        ],
      },
      {
        path: pages.reports.path,
        children: [
          {
            path: '',
            index: true,
            element: (
              <RouteGuard allowedActions={ROLE_ACTIONS.READ} subject='Reports'>
                <Reports />
              </RouteGuard>
            ),
          },
        ],
      },
      {
        path: pages.integrations.path,
        children: [
          {
            path: '',
            index: true,
            element: (
              <RouteGuard
                allowedActions={ROLE_ACTIONS.MANAGE}
                subject='Applications'
              >
                <Integrations />
              </RouteGuard>
            ),
          },
        ],
      },
      {
        path: pages.emailIntegrationLogs.path,
        children: [
          {
            path: '',
            index: true,
            element: (
              <RouteGuard allowedActions={ROLE_ACTIONS.MANAGE} subject='all'>
                <EmailIntegrationLogs />
              </RouteGuard>
            ),
          },
        ],
      },
    ],
  },
];

export default routes;
